import apiClient from '../api-client.js';

/**
 * Service for AI Copilot interactions
 * Handles thread and message management with polling support
 */

/**
 * List all threads for the authenticated user
 * @param {Object} params - Query parameters
 * @param {string} [params.search] - Search term
 * @param {string} [params.ordering] - Sort field
 * @returns {Promise<Array>} List of threads
 */
export const listThreads = async (params = {}) => {
  try {
    const response = await apiClient.get('/copilot/threads/', { params });
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Create a new conversation thread
 * @param {string|null} title - Thread title (optional)
 * @returns {Promise<Object>} Created thread
 */
export const createThread = async (title = null) => {
  try {
    const response = await apiClient.post('/copilot/threads/', { title });
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Get thread details with all messages
 * @param {number} threadId - Thread ID
 * @returns {Promise<Object>} Thread with messages
 */
export const getThread = async (threadId) => {
  try {
    const response = await apiClient.get(`/copilot/threads/${threadId}/`);
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

 /**
 * Update a thread (e.g. rename title)
 * @param {number} threadId - Thread ID
 * @param {Object} data - Fields to update (currently only `title`)
 * @returns {Promise<Object>} Updated thread
 */
export const updateThread = async (threadId, data) => {
  try {
    const response = await apiClient.patch(`/copilot/threads/${threadId}/`, data);
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Delete a thread and all its messages
 * @param {number} threadId - Thread ID
 * @returns {Promise<void>}
 */
export const deleteThread = async (threadId) => {
  try {
    await apiClient.delete(`/copilot/threads/${threadId}/`);
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Send a message to the AI assistant
 * @param {number} threadId - Thread ID
 * @param {string} userInput - User's question
 * @returns {Promise<Object>} Created message (status: pending)
 */
export const sendMessage = async (threadId, userInput) => {
  try {
    const response = await apiClient.post('/copilot/messages/', {
      thread_id: threadId,
      user_input: userInput,
    });
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Poll message to watch response streaming
 * @param {number} messageId - Message ID
 * @returns {Promise<Object>} Message with current status and response
 */
export const pollMessage = async (messageId) => {
  try {
    const response = await apiClient.get(`/copilot/messages/${messageId}/`);
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * List all messages in a thread
 * @param {number} threadId - Thread ID
 * @returns {Promise<Array>} List of messages
 */
export const listMessages = async (threadId) => {
  try {
    const response = await apiClient.get(`/copilot/threads/${threadId}/messages/`);
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Query the Celery task state for a title auto-generation task.
 * @param {string} taskId - Celery task ID returned by the message creation endpoint
 * @returns {Promise<{state: string, title: string|null}>}
 */
export const getTitleTaskStatus = async (taskId) => {
  try {
    const response = await apiClient.get('/copilot/threads/title-task-status/', {
      params: { task_id: taskId },
    });
    return response.data;
  } catch (error) {
    handleCopilotError(error);
  }
};

/**
 * Poll until a thread title is generated.
 *
 * When `taskId` is provided the function queries the Celery task state directly
 * (PENDING → STARTED → SUCCESS/FAILURE/REVOKED) and only surfaces the title once
 * the task succeeds **and** the title has real content.
 *
 * When no `taskId` is given it falls back to polling the thread endpoint and
 * checking the title field – preserving backward compatibility.
 *
 * @param {number} threadId - Thread ID to watch
 * @param {Object} options
 * @param {string|null} [options.taskId] - Celery task ID for the title generation job
 * @param {function} [options.onTitleFound] - Called with the new title when found
 * @param {number} [options.maxPolls=8] - Max attempts
 * @param {number} [options.pollInterval=1500] - ms between polls
 * @param {AbortSignal} [options.signal] - AbortSignal to cancel polling early
 * @returns {Promise<string|null>} The generated title, or null if not found in time
 */
export const pollThreadTitle = async (
  threadId,
  { taskId = null, onTitleFound = null, maxPolls = 8, pollInterval = 1500, signal = null } = {}
) => {
  let polls = 0;

  while (polls < maxPolls) {
    if (signal?.aborted) return null;

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
    polls++;

    if (signal?.aborted) return null;

    if (taskId) {
      // --- Celery-based polling ---
      const { state, title } = await getTitleTaskStatus(taskId);

      // Terminal states: stop polling regardless
      if (state === 'SUCCESS') {
        const hasContent = typeof title === 'string' && title.trim() !== '';
        if (hasContent) {
          if (onTitleFound) onTitleFound(title);
          return title;
        }
        // SUCCESS but empty title (LLM had no context) – nothing to show
        return null;
      }

      if (state === 'FAILURE' || state === 'REVOKED') {
        return null;
      }

      // PENDING / STARTED → keep polling
    } else {
      // --- Fallback: poll DB via thread endpoint ---
      const threadData = await getThread(threadId);
      const title = threadData?.title;
      const hasContent = typeof title === 'string' ? title.trim() !== '' : Boolean(title);

      if (hasContent) {
        if (onTitleFound) onTitleFound(title);
        return title;
      }
    }
  }

  return null;
};

/**
 * Poll message with status checking
 * Continues polling until message is completed or failed
 * @param {number} messageId - Message ID
 * @param {function} onUpdate - Callback for each poll update
 * @param {number} maxPolls - Maximum number of polls (default: 60)
 * @param {number} pollInterval - Interval between polls in ms (default: 2500)
 * @returns {Promise<Object>} Final message state
 */
export const pollUntilComplete = async (
  messageId,
  onUpdate = null,
  maxPolls = 60,
  pollInterval = 2500
) => {
  let polls = 0;
  
  while (polls < maxPolls) {
    const message = await pollMessage(messageId);
    
    // Call update callback if provided
    if (onUpdate) {
      onUpdate(message);
    }
    
    // Check if processing is complete
    if (message.status === 'completed' || message.status === 'failed') {
      return message;
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, pollInterval));
    polls++;
  }
  
  // Max polls reached
  throw new Error('Polling timeout: Response took too long');
};

/**
 * Error handler for copilot API calls
 * @param {Error} error - Error object from axios
 * @throws {Error} Formatted error
 */
const handleCopilotError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        throw new Error(data.detail || 'Datos inválidos en la solicitud');
      case 401:
        throw new Error('No estás autenticado. Por favor, inicia sesión nuevamente.');
      case 403:
        throw new Error('No tienes permisos para usar el copiloto IA. Esta función está disponible solo para Administradores y Consultores.');
      case 404:
        throw new Error('La conversación o mensaje no fue encontrado.');
      case 500:
        throw new Error('Error del servidor. Por favor, intenta nuevamente más tarde.');
      default:
        throw new Error(data.detail || 'Error al procesar la solicitud');
    }
  }
  
  if (error.request) {
    throw new Error('Error de conexión. Verifica tu conexión a internet.');
  }
  
  throw error;
};

export default {
  listThreads,
  createThread,
  getThread,
  updateThread,
  deleteThread,
  sendMessage,
  pollMessage,
  listMessages,
  pollUntilComplete,
  getTitleTaskStatus,
  pollThreadTitle,
};

